/**
 * Jaipur Handloom reusable storefront module — 18AE22
 * File: jh-90-cart.js
 * Purpose: Sole synchronized cart owner for product-form adds, sticky/full-cart rendering, multi-size quantities, notes, coupons, progress and recommendations.
 * Dependencies: Shopify storefront markup and window.JH utilities.
 * Contract: Preserve existing classes, data-jh-* attributes, setting IDs and public events.
 * Maintenance: Extend this owner instead of appending a later patch or replacement controller.
 */
/* Single cart controller for the drawer and full cart page, using Shopify Ajax Cart endpoints. */
(function() {
  'use strict';
  if (window.__JH_CART_OWNER__) return;
  window.__JH_CART_OWNER__ = true;

  var doc = document;
  var html = document.documentElement;
  var state = {
    cart: null,
    busy: false,
    timer: null,
    recSignature: '',
    recBusy: false,
    lastOpener: null,
    productCache: {},
    initialSynced: false,
    mutationQueue: Promise.resolve(),
    pendingMutations: 0,
    refreshTimer: null,
    cartSignature: '',
    syncChannel: null,
    appliedDiscountCode: '',
    appliedDiscountAt: 0,
    couponUpgradePending: false,
    couponUpgradeNoticeCode: '',
    couponUpgradeFailedSignature: '',
    tabId: 'jh-' + Date.now() + '-' + Math.random().toString(36).slice(2)
  };

  var NAVIGATION_WAIT_MS = 6000;

  function qs(sel, root) {
    return (root || doc).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || doc).querySelectorAll(sel));
  }

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function(ch) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      } [ch];
    });
  }

  function stripTags(value) {
    return String(value == null ? '' : value).replace(/<[^>]*>/g, '');
  }

  function rootUrl() {
    var root = '/';
    try {
      if (window.Shopify && Shopify.routes && Shopify.routes.root) root = Shopify.routes.root;
    } catch (e) {}
    if (!root) root = '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  function route(path) {
    path = String(path || '').replace(/^\/+/, '');
    return rootUrl() + path;
  }

  function money(cents) {
    cents = Number(cents || 0);
    try {
      if (window.Shopify && Shopify.formatMoney) return stripTags(Shopify.formatMoney(cents));
    } catch (e) {}
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'
      });
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function debounce(fn, wait) {
    var t = null;
    var args = null;
    var ctx = null;

    function invoke() {
      if (!args) return;
      var callArgs = args;
      var callCtx = ctx;
      args = null;
      ctx = null;
      t = null;
      fn.apply(callCtx, callArgs);
    }

    function debounced() {
      args = arguments;
      ctx = this;
      clearTimeout(t);
      t = setTimeout(invoke, wait || 250);
    }

    debounced.flush = function() {
      if (!t) return;
      clearTimeout(t);
      invoke();
    };

    debounced.cancel = function() {
      clearTimeout(t);
      t = null;
      args = null;
      ctx = null;
    };

    return debounced;
  }

  function request(url, options) {
    options = options || {};
    options.headers = options.headers || {};
    options.headers.Accept = 'application/json';
    return fetch(url, options).then(function(res) {
      if (!res.ok) throw new Error('Cart request failed: ' + res.status);
      return res.json();
    });
  }

  function getCart() {
    return request(route('cart.js') + '?t=' + Date.now(), {
      cache: 'no-store'
    });
  }

  function queueMutation(task) {
    state.pendingMutations += 1;
    setBusy(true);

    var queued = state.mutationQueue.catch(function() {}).then(task);
    state.mutationQueue = queued.catch(function() {});

    return queued.finally(function() {
      state.pendingMutations = Math.max(0, state.pendingMutations - 1);
      if (!state.pendingMutations) setBusy(false);
    });
  }

  function whenCartIdle() {
    return state.mutationQueue.catch(function() {});
  }

  function changeItem(key, quantity) {
    return queueMutation(function() {
      return request(route('cart/change.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: Math.max(0, parseInt(quantity, 10) || 0)
        })
      }).then(sync).catch(recover);
    });
  }

  function addItems(items, options) {
    options = options || {};
    items = (items || []).map(function(item) {
      var normalized = {
        id: Number(item && item.id),
        quantity: Math.max(1, parseInt(item && item.quantity, 10) || 1)
      };
      if (item && item.properties) normalized.properties = item.properties;
      if (item && item.selling_plan) normalized.selling_plan = item.selling_plan;
      return normalized;
    }).filter(function(item) {
      return !!item.id;
    });

    if (!items.length) return Promise.reject(new Error('Missing cart items'));
    return queueMutation(function() {
      return request(route('cart/add.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: items })
      }).then(getCart).then(function(cart) {
        sync(cart);
        if (options.openDrawer !== false) openDrawer(options.opener);
        return cart;
      }).catch(function(error) {
        return recover(error).then(function() {
          throw error;
        });
      });
    });
  }

  function addVariant(id, quantity, options) {
    if (!id) return Promise.reject(new Error('Missing variant id'));
    return addItems([{
      id: id,
      quantity: quantity
    }], options);
  }

  function addVariantSilently(id, quantity) {
    return addVariant(id, quantity, { openDrawer: false });
  }

  function addForm(form) {
    var fd = new FormData(form);
    return queueMutation(function() {
      return fetch(route('cart/add.js'), {
          method: 'POST',
          headers: {
            Accept: 'application/json'
          },
          body: fd
        })
        .then(function(res) {
          if (!res.ok) throw new Error('Add failed: ' + res.status);
          return res.json();
        })
        .then(getCart).then(function(cart) {
          sync(cart);
          openDrawer(document.activeElement);
          return cart;
        }).catch(function(err) {
          recover(err);
          try {
            form.submit();
          } catch (e) {}
        });
    });
  }

  function updateNote(note) {
    return queueMutation(function() {
      return request(route('cart/update.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          note: String(note || '')
        })
      }).then(sync).catch(function() {});
    });
  }

  function setBusy(on) {
    state.busy = !!on;
    qsa('[data-jh-smart-cart-page],[data-jh-smart-cart-drawer]').forEach(function(el) {
      el.classList.toggle('jh-smart-loading', !!on);
    });
  }

  function openDrawer(opener) {
    var drawer = qs('[data-jh-smart-cart-drawer]') || qs('[data-jh-cart-drawer]');
    var backdrop = qs('[data-jh-drawer-backdrop]');
    state.lastOpener = opener || document.activeElement;
    if (drawer) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
    }
    if (backdrop) backdrop.classList.add('is-open');
    html.classList.add('jh-cart-open', 'jh-smart-cart-open');
    if (drawer && window.JH && JH.A11y) {
      JH.A11y.activateDialog(drawer, state.lastOpener, qs('[data-jh-smart-cart-close]', drawer));
    }
  }

  function closeDrawer() {
    var drawer = qs('[data-jh-smart-cart-drawer]') || qs('[data-jh-cart-drawer]');
    var backdrop = qs('[data-jh-drawer-backdrop]');
    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }
    if (backdrop) backdrop.classList.remove('is-open');
    html.classList.remove('jh-cart-open', 'jh-smart-cart-open');
    if (drawer && window.JH && JH.A11y) JH.A11y.deactivateDialog(drawer, state.lastOpener);
    state.lastOpener = null;
  }

  function productHandle(item) {
    if (item.handle) return item.handle;
    var m = String(item.url || '').match(/\/products\/([^?#/]+)/);
    return m ? m[1] : '';
  }

  function isRug(item) {
    var hay = [item.product_type, item.product_title, item.title, item.handle, item.url].join(' ').toLowerCase();
    return /\brug(s)?\b|\bdoormat(s)?\b|\bdoor mat(s)?\b|\brunner(s)?\b|\bdhurrie(s)?\b|\bdurrie(s)?\b/.test(hay);
  }

  function productTextFromCartItem(item) {
    return [item.product_type, item.product_title, item.title, item.handle, item.url, item.variant_title].join(' ').toLowerCase();
  }

  function productTextFromRecommendation(product) {
    var tags = Array.isArray(product.tags) ? product.tags.join(' ') : (product.tags || '');
    return [product.product_type, product.type, product.title, product.handle, tags].join(' ').toLowerCase();
  }

  function isRugText(text) {
    return /rug|dhurrie|durrie|runner|doormat|door mat|mat\b/.test(String(text || '').toLowerCase());
  }

  function isDecorText(text) {
    return /pillow|cushion|throw|kantha|quilt|pouf|cover|decor|accent|sham/.test(String(text || '').toLowerCase());
  }

  function isSmallAddonText(text) {
    return /doormat|door mat|runner|small|pillow|cushion|cover|throw|kantha|quilt|accent|mat\b/.test(String(text || '').toLowerCase());
  }

  function isCartItemRug(item) {
    return isRugText(productTextFromCartItem(item));
  }

  function firstAvailableVariant(product) {
    var variants = product && product.variants || [];
    for (var i = 0; i < variants.length; i++)
      if (variants[i] && variants[i].available) return variants[i];
    return variants[0] || null;
  }

  function productImage(product) {
    if (!product) return '';
    if (product.featured_image) return product.featured_image;
    if (product.images && product.images.length) return product.images[0];
    if (product.media && product.media.length && product.media[0].src) return product.media[0].src;
    return '';
  }

  function recUrl(product) {
    if (!product) return '#';
    if (product.url) return product.url;
    if (product.handle) return route('products/' + product.handle);
    return '#';
  }


  function renderRecommendationCard(product, reason) {
    var variant = firstAvailableVariant(product);
    var title = product && product.title || 'Recommended product';
    var handle = product && product.handle || '';
    var image = productImage(product);
    var price = Number((variant && variant.price) || product.price_min || product.price || 0);
    var compare = Number((variant && variant.compare_at_price) || product.compare_at_price_min || product.compare_at_price || 0);
    var href = recUrl(product);
    return '<article class="jh-smart-rec-card jh-qa5-rec-card" data-jh-smart-rec-card data-dynamic-rec="true" data-product-handle="' + esc(handle) + '" data-product-id="' + esc(product.id || '') + '">' +
      '<a class="jh-smart-rec-card__media" href="' + esc(href) + '">' + (image ? '<img src="' + esc(image) + '" alt="' + esc(title) + '" loading="lazy">' : '') + '</a>' +
      '<div class="jh-smart-rec-card__body">' +
      (reason ? '<span class="jh-smart-rec-card__reason">' + esc(reason) + '</span>' : '') +
      '<a class="jh-smart-rec-card__title" href="' + esc(href) + '">' + esc(title) + '</a>' +
      '<p class="jh-smart-rec-card__price" data-jh-smart-rec-price>' + money(price) + (compare > price ? '<s>' + money(compare) + '</s>' : '') + '</p>' +
      '<a class="jh-smart-rec-card__button" href="' + esc(href) + '">' + esc(document.body.getAttribute('data-jh-recommendation-cta') || 'View details') + '</a>' +
      '</div></article>';
  }

  function fetchProductRecommendations(productId, intent) {
    if (!productId) return Promise.resolve([]);
    var url = route('recommendations/products.json') + '?product_id=' + encodeURIComponent(productId) + '&limit=10&intent=' + encodeURIComponent(intent || 'related');
    return request(url, {
      cache: 'no-store'
    }).then(function(data) {
      return data && data.products || [];
    }).catch(function() {
      return [];
    });
  }

  function uniqueProducts(products, used, filterFn, limit) {
    var out = [];
    (products || []).forEach(function(product) {
      if (!product || !product.available) return;
      var handle = product.handle || '';
      var key = handle || String(product.id || '');
      if (!key || used[key]) return;
      if (filterFn && !filterFn(product)) return;
      used[key] = true;
      out.push(product);
    });
    return out.slice(0, limit || 4);
  }

  function productPoolFromRecommendations(cart, intents) {
    var candidates = (cart.items || []).filter(function(item) {
      return item && item.product_id;
    });
    candidates.sort(function(a, b) {
      return (b.final_line_price || b.line_price || 0) - (a.final_line_price || a.line_price || 0);
    });
    candidates = candidates.slice(0, 4);
    var jobs = [];
    candidates.forEach(function(item) {
      (intents || ['related']).forEach(function(intent) {
        jobs.push(fetchProductRecommendations(item.product_id, intent));
      });
    });
    return Promise.all(jobs).then(function(groups) {
      var flat = [];
      groups.forEach(function(group) {
        (group || []).forEach(function(product) {
          flat.push(product);
        });
      });
      return flat;
    });
  }

  function updateSmartRecommendations(cart) {
    var rails = qsa('[data-jh-dynamic-rec-rail]');
    if (!rails.length || !cart || !cart.items || !cart.items.length || state.recBusy) return;
    var signature = cart.items.map(function(item) {
      return [item.product_id, productHandle(item)].join(':');
    }).join('|');
    if (signature && signature === state.recSignature) return;
    state.recSignature = signature;
    state.recBusy = true;
    var used = {};
    cart.items.forEach(function(item) {
      var h = productHandle(item);
      if (h) used[h] = true;
      if (item.product_id) used[String(item.product_id)] = true;
    });
    productPoolFromRecommendations(cart, ['complementary', 'related']).then(function(pool) {
      if (!pool || !pool.length) return;
      var addRug = uniqueProducts(pool, used, function(p) {
        return isRugText(productTextFromRecommendation(p));
      }, 8);
      var groups = {
        complete_room: { products: addRug.slice(0, 4), reason: 'Rug pairing' },
        add_rug: { products: addRug.slice(0, 4), reason: 'Rug pairing' },
        small_addons: { products: addRug.slice(0, 4), reason: 'Rug pairing' },
        drawer: { products: addRug.slice(0, 4), reason: 'Rug pairing' }
      };
      rails.forEach(function(rail) {
        var type = rail.getAttribute('data-jh-dynamic-rec-rail') || 'drawer';
        var group = groups[type];
        if (!group || !group.products || !group.products.length) return; // Keep Liquid fallbacks.
        rail.innerHTML = group.products.map(function(product) {
          return renderRecommendationCard(product, group.reason);
        }).join('');
      });
      hideCartedRecommendations(cart);
    }).finally(function() {
      state.recBusy = false;
    });
  }

  function renderLine(item, context) {
    var key = esc(item.key || '');
    var imgObj = item.featured_image || {};
    var img = item.image || imgObj.url || imgObj.src || '';
    var title = item.product_title || item.title || 'Cart item';
    var rawVariantTitle = item.variant_title || '';
    var currentVariantTitle = rawVariantTitle && rawVariantTitle !== 'Default Title' ? rawVariantTitle : '';
    var props = '';
    if (item.properties) {
      Object.keys(item.properties).forEach(function(name) {
        if (!name || name.charAt(0) === '_' || item.properties[name] == null || item.properties[name] === '') return;
        props += '<li>' + esc(name) + ': ' + esc(item.properties[name]) + '</li>';
      });
      if (props) props = '<ul class="jh-smart-cart-line__props">' + props + '</ul>';
    }
    var imageHtml = img ? '<img src="' + esc(img) + '" alt="' + esc(title) + '" loading="lazy">' : '';
    var pageClass = context === 'page' ? ' jh-smart-cart-line--page' : '';
    var price = money(item.final_line_price || item.line_price || 0);
    var original = item.original_line_price && item.original_line_price > (item.final_line_price || item.line_price || 0) ? '<s>' + money(item.original_line_price) + '</s>' : '';
    var handle = productHandle(item);
    var quantityControls = '<div class="jh-smart-qty" data-jh-smart-qty data-key="' + key + '"><button type="button" data-jh-smart-qty-minus aria-label="Decrease quantity for ' + esc(title) + '">−</button><input type="number" min="0" inputmode="numeric" value="' + esc(item.quantity || 0) + '" data-jh-smart-qty-input aria-label="Quantity for ' + esc(title) + '"><button type="button" data-jh-smart-qty-plus aria-label="Increase quantity for ' + esc(title) + '">+</button></div>';
    var currentSelection = '<div class="jh-smart-cart-line__current-selection jh-qa5-cart-selection" data-jh-cart-current-selection>' + (currentVariantTitle ? '<p class="jh-qa5-cart-variant"><span>Selected</span><strong data-jh-current-variant-text>' + esc(currentVariantTitle) + '</strong></p>' : '') + '<div class="jh-qa5-cart-actions">' + quantityControls + '<button class="jh-smart-cart-remove" type="button" data-jh-smart-remove data-key="' + key + '" aria-label="Remove ' + esc(title) + ' from cart">Remove</button>' + (context === 'drawer' ? '<strong class="jh-smart-cart-line__current-price">' + price + '</strong>' : '') + '</div><p class="jh-qa5-cart-size-note">Need a different size? Open the rug page to choose another variation.</p></div>';
    return '<article class="jh-smart-cart-line' + pageClass + '" data-jh-smart-cart-line data-key="' + key + '" data-product-id="' + esc(item.product_id || '') + '" data-product-handle="' + esc(handle) + '">' + '<a class="jh-smart-cart-line__image" href="' + esc(item.url || '#') + '">' + imageHtml + '</a>' + '<div class="jh-smart-cart-line__body"><a class="jh-smart-cart-line__title" href="' + esc(item.url || '#') + '">' + esc(title) + '</a>' + currentSelection + props + '</div>' + (context === 'page' ? '<div class="jh-smart-cart-line__price"><strong>' + price + '</strong>' + original + '</div>' : '') + '</article>';
  }

  function cartItemByKey(key) {
    var items = state.cart && state.cart.items || [];
    for (var i = 0; i < items.length; i += 1) {
      if (String(items[i].key) === String(key)) return items[i];
    }
    return null;
  }

  function fetchProductByHandle(handle) {
    handle = String(handle || '');
    if (!handle) return Promise.resolve(null);
    if (state.productCache[handle]) return state.productCache[handle];

    var encoded = encodeURIComponent(handle);
    var urls = [
      route('products/' + encoded + '.js'),
      '/products/' + encoded + '.js',
      route('products/' + encoded + '.json'),
      '/products/' + encoded + '.json'
    ].filter(function(url, index, list) {
      return list.indexOf(url) === index;
    });

    function tryUrl(index) {
      if (index >= urls.length) return Promise.resolve(null);
      return request(urls[index], { cache: 'force-cache' }).then(function(data) {
        var product = data && data.product ? data.product : data;
        if (!product || !product.variants || !product.variants.length) return tryUrl(index + 1);
        return product;
      }).catch(function() {
        return tryUrl(index + 1);
      });
    }

    state.productCache[handle] = tryUrl(0).then(function(product) {
      if (!product) delete state.productCache[handle];
      return product;
    });
    return state.productCache[handle];
  }

  function hydrateCartVariantSelects(scope) {
    qsa('[data-jh-smart-cart-line]', scope || doc).forEach(function(line) {
      var summary = qs('[data-jh-cart-variant-summary]', line);
      if (!summary || summary.getAttribute('data-hydrated') === 'true') return;

      var handle = summary.getAttribute('data-product-handle') || line.getAttribute('data-product-handle');
      var currentId = summary.getAttribute('data-current-variant-id');
      var picker = qs('[data-jh-cart-variant-picker]', line);
      var menuOptions = qs('[data-jh-cart-variant-menu] .jh-cart-variant-menu__options', line);
      var addWrap = qs('[data-jh-cart-add-variant]', line);
      var addSummary = qs('[data-jh-cart-add-variant-summary]', line);
      var addOptions = qs('[data-jh-cart-add-variant-menu] .jh-cart-variant-menu__options', line);
      var addButton = qs('[data-jh-cart-add-size]', line);

      function finishCurrentFallback() {
        if (!document.documentElement.contains(summary)) return;
        summary.setAttribute('data-hydrated', 'fallback');
        summary.setAttribute('aria-busy', 'false');
        summary.setAttribute('aria-disabled', 'true');
        if (picker) picker.classList.add('is-static');
        if (addWrap) addWrap.hidden = true;
      }

      fetchProductByHandle(handle).then(function(product) {
        if (!product || !product.variants || !product.variants.length) {
          finishCurrentFallback();
          return;
        }

        var currentButtons = product.variants.filter(function(variant) {
          return variant && (variant.available || String(variant.id) === String(currentId));
        }).map(function(variant) {
          var label = variant.title || variant.name || 'Option';
          var selected = String(variant.id) === String(currentId);
          return '<button type="button" role="option" class="jh-cart-variant-menu__option' + (selected ? ' is-selected' : '') + '" data-jh-cart-variant-option data-key="' + esc(line.getAttribute('data-key') || '') + '" data-variant-id="' + esc(variant.id) + '" data-variant-title="' + esc(label) + '" aria-selected="' + (selected ? 'true' : 'false') + '"' + (variant.available === false ? ' disabled' : '') + '><span>' + esc(label) + '</span>' + (variant.available === false ? '<small>Sold out</small>' : '') + '</button>';
        }).join('');

        if (!document.documentElement.contains(summary)) return;
        if (currentButtons && menuOptions) menuOptions.innerHTML = currentButtons;
        summary.setAttribute('data-hydrated', 'true');
        summary.setAttribute('aria-busy', 'false');
        summary.removeAttribute('aria-disabled');
        if (picker) picker.classList.remove('is-static');

        if (!addSummary || !addOptions || !addWrap) return;
        var alternateButtons = product.variants.filter(function(variant) {
          return variant && variant.available && String(variant.id) !== String(currentId);
        }).map(function(variant) {
          var label = variant.title || variant.name || 'Option';
          return '<button type="button" role="option" class="jh-cart-variant-menu__option" data-jh-cart-add-variant-option data-key="' + esc(line.getAttribute('data-key') || '') + '" data-variant-id="' + esc(variant.id) + '" data-variant-title="' + esc(label) + '" aria-selected="false"><span>' + esc(label) + '</span><small>' + esc(money(variant.price || 0)) + '</small></button>';
        }).join('');

        if (alternateButtons) {
          addOptions.innerHTML = alternateButtons;
          addSummary.setAttribute('data-hydrated', 'true');
          addSummary.setAttribute('aria-busy', 'false');
          addSummary.removeAttribute('aria-disabled');
          addWrap.hidden = false;
          if (addButton) {
            addButton.setAttribute('data-selected-variant-id', '');
            addButton.disabled = true;
          }
        } else {
          addWrap.hidden = true;
        }
      }).catch(function() {
        finishCurrentFallback();
      });
    });
  }

  function replaceItemVariant(key, newVariantId, select) {
    function run() {
      var item = cartItemByKey(key);
      if (!item || !newVariantId || String(item.variant_id || item.id) === String(newVariantId)) return Promise.resolve(state.cart);
      var oldVariantId = item.variant_id || item.id;
      var oldValue = select ? String(oldVariantId) : '';
      var addItem = {
        id: Number(newVariantId),
        quantity: Math.max(1, Number(item.quantity || 1))
      };
      if (item.properties && Object.keys(item.properties).length) addItem.properties = item.properties;
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) addItem.selling_plan = item.selling_plan_allocation.selling_plan.id;
      if (select) {
        select.disabled = true;
        select.setAttribute('aria-busy', 'true');
      }
      qsa('[data-jh-cart-status]').forEach(function(status) { status.textContent = 'Updating size or variation…'; });
      return request(route('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [addItem] })
      }).then(function() {
        return request(route('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
      }).then(function(cart) {
        sync(cart);
        qsa('[data-jh-cart-status]').forEach(function(status) { status.textContent = 'Size or variation updated in both cart views.'; });
        return cart;
      }).catch(function(error) {
        if (select && document.documentElement.contains(select)) select.value = oldValue;
        return recover(error).then(function() {
          throw error;
        });
      }).finally(function() {
        if (select && document.documentElement.contains(select)) {
          select.disabled = false;
          select.setAttribute('aria-busy', 'false');
        }
      });
    }

    return queueMutation(function() {
      if (!state.cart || !state.cart.items) return getCart().then(sync).then(run);
      return run();
    });
  }

  function addAnotherVariantFromLine(key, newVariantId, quantity, button) {
    var item = cartItemByKey(key);
    if (!item || !newVariantId) return Promise.reject(new Error('Unable to find this cart item.'));

    var payload = {
      id: Number(newVariantId),
      quantity: Math.max(1, parseInt(quantity, 10) || 1)
    };
    if (item.properties && Object.keys(item.properties).length) payload.properties = item.properties;
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) payload.selling_plan = item.selling_plan_allocation.selling_plan.id;

    var oldText = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding…';
    }
    qsa('[data-jh-cart-status]').forEach(function(status) {
      status.textContent = 'Adding another size to your cart…';
    });

    return addItems([payload], { openDrawer: false }).then(function(cart) {
      qsa('[data-jh-cart-status]').forEach(function(status) {
        status.textContent = 'Another size was added to your cart.';
      });
      return cart;
    }).finally(function() {
      if (button && document.documentElement.contains(button)) {
        button.disabled = false;
        button.textContent = oldText || 'Add size';
      }
    });
  }

  function cartMarkupMatches(cart) {
    var boxes = qsa('[data-jh-smart-cart-lines]');
    if (!boxes.length) return false;
    var expected = (cart && cart.items || []).map(function(item) { return String(item.key || ''); });
    return boxes.every(function(box) {
      var actual = qsa('[data-jh-smart-cart-line]', box).map(function(line) {
        return String(line.getAttribute('data-key') || '');
      });
      if (actual.length !== expected.length) return false;
      for (var i = 0; i < expected.length; i += 1) {
        if (actual[i] !== expected[i]) return false;
      }
      return true;
    });
  }

  function emptyHtml(context) {
    var page = context === 'page';
    return '<div class="jh-smart-cart-empty' + (page ? ' jh-smart-cart-empty--page' : '') + '"><h2>Your cart is empty.</h2><p>Start with a handmade rug, then add matching decor before checkout.</p><a class="jh-smart-cart-checkout" href="' + route('collections') + '">Shop collections</a></div>';
  }

  function renderLines(cart) {
    cart = cart || {
      items: []
    };
    qsa('[data-jh-smart-cart-lines]').forEach(function(box) {
      var context = box.getAttribute('data-jh-smart-cart-lines') || 'drawer';
      if (!cart.items || !cart.items.length) box.innerHTML = emptyHtml(context);
      else box.innerHTML = cart.items.map(function(item) {
        return renderLine(item, context);
      }).join('');
      hydrateCartVariantSelects(box);
    });
    qsa('[data-jh-smart-cart-shell]').forEach(function(shell) {
      shell.classList.toggle('is-empty', !cart.item_count);
    });
  }

  function updateCounts(cart) {
    qsa('[data-jh-cart-count],[data-jh-mini-cart-count],[data-jh-smart-cart-count],[data-cart-count],.cart-count,#CartCount').forEach(function(el) {
      el.textContent = cart.item_count || 0;
    });
    qsa('[data-jh-cart-drawer-total],[data-jh-smart-cart-total],[data-jh-mini-cart-total]').forEach(function(el) {
      el.textContent = money(cart.total_price || 0);
    });
    var saved = discountAmount(cart);
    qsa('[data-jh-smart-cart-discount-row]').forEach(function(row) {
      row.hidden = saved <= 0;
    });
    qsa('[data-jh-smart-cart-discount]').forEach(function(el) {
      el.textContent = saved > 0 ? '−' + money(saved) : money(0);
    });
  }

  function updateProgress(cart) {
    qsa('[data-jh-progress]').forEach(function(el) {
      var alwaysFree = el.getAttribute('data-free-shipping-always') === 'true';
      var success = el.getAttribute('data-success-text') || 'You received free shipping.';
      if (alwaysFree) {
        el.style.setProperty('--jh-progress', '100%');
        var freeMsg = qs('[data-jh-progress-message]', el);
        if (freeMsg) freeMsg.textContent = success;
        var freeTrack = qs('[role="progressbar"]', el);
        if (freeTrack) freeTrack.setAttribute('aria-valuenow', '100');
        return;
      }
      var threshold = parseInt(el.getAttribute('data-threshold-cents'), 10) || 0;
      if (!threshold) return;
      var total = Number(cart.total_price || 0);
      var pct = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));
      var remaining = Math.max(0, threshold - total);
      var remainingText = el.getAttribute('data-remaining-text') || 'You received free shipping.';
      var text = remaining <= 0 ? success : remainingText.replace(/\{\{\s*amount\s*\}\}/g, money(remaining));
      el.style.setProperty('--jh-progress', pct + '%');
      var msg = qs('[data-jh-progress-message]', el);
      if (msg) msg.textContent = text;
      var track = qs('[role="progressbar"]', el);
      if (track) track.setAttribute('aria-valuenow', String(pct));
    });
  }

  function normalizeDiscountCode(value) {
    return String(value == null ? '' : value).trim().slice(0, 100);
  }

  function clearAppliedDiscountState() {
    var previousCode = normalizeDiscountCode(state.appliedDiscountCode).toLowerCase();
    state.appliedDiscountCode = '';
    state.appliedDiscountAt = 0;
    if (previousCode) {
      qsa('[data-jh-smart-coupon-input]').forEach(function(input) {
        if (normalizeDiscountCode(input.value).toLowerCase() === previousCode) input.value = '';
      });
    }
  }

  function persistAppliedDiscount(code) {
    code = normalizeDiscountCode(code);
    if (!code) return;
    state.appliedDiscountCode = code;
    state.appliedDiscountAt = Date.now();
  }

  function updateCouponEntries(code, applied) {
    qsa('[data-jh-smart-coupon-entry],.jh-smart-coupon-entry').forEach(function(entry) {
      var input = qs('[data-jh-smart-coupon-input]', entry);
      var button = qs('[data-jh-smart-coupon-apply]', entry);
      if (input && applied && code) input.value = code;
      if (button) {
        button.disabled = false;
        button.removeAttribute('data-jh-applying');
        button.textContent = applied && code ? 'Applied' : 'Apply';
      }
    });
  }

  function validateAppliedDiscount(cart) {
    var code = normalizeDiscountCode(state.appliedDiscountCode);
    if (!code) return;
    if (!(cart && cart.item_count)) {
      clearAppliedDiscountState();
      return;
    }

    // Shopify is the only source of truth for discount eligibility. Never
    // hard-code future coupon rules in the theme. Keep the UI applied only
    // while Shopify reports the code or an actual cart saving.
    if (!cartExplicitlyHasDiscount(cart, code) && discountAmount(cart) <= 0) {
      clearAppliedDiscountState();
    }
  }

  function withNavigationTimeout(promise, timeoutMs) {
    return new Promise(function(resolve) {
      var finished = false;
      var timer = window.setTimeout(done, timeoutMs || NAVIGATION_WAIT_MS);
      function done() {
        if (finished) return;
        finished = true;
        window.clearTimeout(timer);
        resolve();
      }
      Promise.resolve(promise).catch(function() {}).then(done);
    });
  }

  function settleCartBeforeNavigation(refreshFromServer) {
    debouncedQty.flush();
    debouncedNote.flush();
    var pending = whenCartIdle();
    if (refreshFromServer) pending = pending.then(getCart).then(sync);
    return withNavigationTimeout(pending, NAVIGATION_WAIT_MS);
  }

  function updateCoupons(cart) {
    validateAppliedDiscount(cart);
    var details = couponUpgradeDetails(cart);
    var rugQty = details.rugQty;
    var upgrading = scheduleLowerCouponRemoval(cart);
    var appliedCode = normalizeDiscountCode(state.appliedDiscountCode || inferAppliedDiscountCode(cart));
    var appliedLower = appliedCode.toLowerCase();
    var twoApplied = !!details.two && appliedLower === details.two.toLowerCase();

    qsa('[data-jh-smart-coupon]').forEach(function(box) {
      var one = normalizeDiscountCode(box.getAttribute('data-one-code') || details.one || '10OFF');
      var two = normalizeDiscountCode(box.getAttribute('data-two-code') || details.two || '20OFF');
      var oneMsg = box.getAttribute('data-one-message') || 'Use 10OFF for 10% off all store items. Add one more rug to unlock 20OFF on rugs.';
      var twoMsg = box.getAttribute('data-two-message') || 'You have 2 or more rugs. Use 20OFF for 20% off eligible rugs.';
      var code = one;
      var title = '10% storewide coupon available';
      var msg = 'Use ' + one + ' for 10% off all items in the store.';
      var unlocked = false;

      if (rugQty >= 2 && !twoApplied) {
        code = two;
        title = '20% OFF coupon unlocked';
        unlocked = true;
        if (state.couponUpgradePending || upgrading) {
          msg = 'You unlocked ' + two + '. Removing ' + one + ' so the higher-saving rug coupon can be used.';
        } else if (state.couponUpgradeFailedSignature === state.cartSignature && details.hasOne) {
          msg = two + ' is unlocked. Copy it and apply it; Shopify will replace ' + one + ' with the higher-saving code.';
        } else if (state.couponUpgradeNoticeCode && state.couponUpgradeNoticeCode.toLowerCase() === two.toLowerCase()) {
          msg = one + ' was removed. Copy ' + two + ' for 20% off eligible rugs.';
        } else if (appliedCode && appliedLower !== one.toLowerCase()) {
          msg = twoMsg + ' Another coupon (' + appliedCode + ') is currently active; applying ' + two + ' may replace it unless Shopify allows both.';
        } else {
          msg = twoMsg + ' Copy ' + two + ' for the higher saving.';
        }
      } else if (twoApplied) {
        code = appliedCode;
        title = '20% rug coupon applied';
        var twoSaving = discountAmount(cart);
        msg = twoSaving > 0 ?
          'Code ' + appliedCode + ' is applied. Current cart savings: ' + money(twoSaving) + '.' :
          'Code ' + appliedCode + ' was accepted by Shopify. Its eligible rug saving will be reflected in checkout totals.';
      } else if (appliedCode) {
        code = appliedCode;
        title = 'Coupon applied';
        var appliedSaving = discountAmount(cart);
        msg = appliedSaving > 0 ?
          'Code ' + appliedCode + ' is applied. Current cart savings: ' + money(appliedSaving) + '.' :
          'Code ' + appliedCode + ' was accepted by Shopify. Any shipping or checkout-only benefit will appear at checkout.';
      } else if (rugQty === 1) {
        code = one;
        title = '10% storewide coupon available';
        msg = oneMsg;
      } else if (!(cart.item_count || 0)) {
        title = 'Coupon field ready';
        msg = 'Add an item, then enter any active Shopify discount code.';
      }

      box.classList.toggle('is-unlocked', unlocked);
      box.classList.toggle('is-upgrading', !!state.couponUpgradePending);
      var titleEl = qs('[data-jh-smart-coupon-title]', box);
      if (titleEl) titleEl.textContent = title;
      var msgEl = qs('[data-jh-smart-coupon-message]', box);
      if (msgEl) msgEl.textContent = msg;
      var codeEl = qs('[data-jh-smart-coupon-code]', box);
      if (codeEl) codeEl.textContent = code;
      var copyButton = qs('[data-jh-smart-copy]', box);
      if (copyButton && copyButton.getAttribute('data-jh-copying') !== 'true') copyButton.textContent = code ? 'Copy ' + code : 'Copy';
      box.hidden = false;
    });

    if (state.couponUpgradePending) {
      updateCouponEntries('', false);
      qsa('[data-jh-smart-coupon-apply]').forEach(function(button) {
        button.disabled = true;
        button.textContent = 'Updating…';
      });
    } else updateCouponEntries(appliedCode, !!appliedCode);
  }

  function hideCartedRecommendations(cart) {
    var handles = {};
    (cart.items || []).forEach(function(item) {
      var h = productHandle(item);
      if (h) handles[h] = true;
    });
    qsa('[data-jh-smart-rec-card][data-product-handle]').forEach(function(card) {
      var h = card.getAttribute('data-product-handle');
      card.hidden = !!(h && handles[h]);
    });
  }

  function cartSignature(cart) {
    var items = (cart && cart.items) || [];
    return items.map(function(item) {
      return [item.key || item.id || '', item.quantity || 0, item.final_line_price || item.line_price || 0].join(':');
    }).join('|') + '//' + Number(cart && cart.item_count || 0) + '//' + Number(cart && cart.total_price || 0);
  }

  function announceCart(cart) {
    var payload = {
      source: state.tabId,
      signature: state.cartSignature,
      at: Date.now()
    };
    try {
      localStorage.setItem('jh_cart_sync_v1', JSON.stringify(payload));
    } catch (error) {}
    try {
      if (state.syncChannel) state.syncChannel.postMessage(payload);
    } catch (error) {}
  }

  function refreshCartFromServer() {
    return whenCartIdle().then(function() {
      return getCart();
    }).then(sync).catch(function(error) {
      try {
        console.warn('[JH cart sync]', error);
      } catch (e) {}
    });
  }

  function scheduleCartRefresh(delay) {
    clearTimeout(state.refreshTimer);
    state.refreshTimer = setTimeout(refreshCartFromServer, typeof delay === 'number' ? delay : 80);
  }

  function bindCartSynchronization() {
    if ('BroadcastChannel' in window) {
      try {
        state.syncChannel = new BroadcastChannel('jh-cart-sync');
        state.syncChannel.addEventListener('message', function(event) {
          var data = event && event.data || {};
          if (!data || data.source === state.tabId || data.signature === state.cartSignature) return;
          scheduleCartRefresh(20);
        });
      } catch (error) {
        state.syncChannel = null;
      }
    }

    window.addEventListener('storage', function(event) {
      if (event.key !== 'jh_cart_sync_v1' || !event.newValue) return;
      try {
        var data = JSON.parse(event.newValue);
        if (data.source === state.tabId || data.signature === state.cartSignature) return;
      } catch (error) {}
      scheduleCartRefresh(20);
    });

    window.addEventListener('pageshow', function(event) {
      // The normal first pageshow occurs alongside the initial cart fetch.
      // Refresh only when restoring from the back-forward cache.
      if (event && event.persisted) scheduleCartRefresh(0);
    });
    window.addEventListener('focus', function() {
      scheduleCartRefresh(60);
    });
    doc.addEventListener('visibilitychange', function() {
      if (!doc.hidden) scheduleCartRefresh(60);
    });
  }

  function sync(cart) {
    state.cart = cart || state.cart || {
      items: [],
      item_count: 0,
      total_price: 0
    };
    state.cartSignature = cartSignature(state.cart);
    var inferredDiscountCode = inferAppliedDiscountCode(state.cart);
    if (inferredDiscountCode) persistAppliedDiscount(inferredDiscountCode);
    updateCounts(state.cart);
    var preserveServerLines = !state.initialSynced && cartMarkupMatches(state.cart);
    state.initialSynced = true;
    if (preserveServerLines) hydrateCartVariantSelects(doc);
    else renderLines(state.cart);
    updateProgress(state.cart);
    updateCoupons(state.cart);
    hideCartedRecommendations(state.cart);
    updateSmartRecommendations(state.cart);
    qsa('[data-jh-cart-status]').forEach(function(status) {
      status.textContent = (state.cart.item_count || 0) + ((state.cart.item_count || 0) === 1 ? ' item in cart.' : ' items in cart.') + ' Subtotal ' + money(state.cart.total_price || 0) + '.';
    });
    try {
      doc.dispatchEvent(new CustomEvent('jh:cart:changed', {
        detail: state.cart
      }));
    } catch (e) {}
    try {
      doc.dispatchEvent(new CustomEvent('cart:updated', {
        detail: state.cart
      }));
    } catch (e) {}
    announceCart(state.cart);
    return state.cart;
  }

  function recover(err) {
    try {
      console.warn('[JH smart cart]', err);
    } catch (e) {}
    return getCart().then(sync).catch(function() {});
  }

  function discountAmount(cart) {
    if (!cart) return 0;
    var direct = Number(cart.total_discount || 0);
    var calculated = Math.max(0, Number(cart.original_total_price || 0) - Number(cart.total_price || 0));
    return Math.max(direct, calculated);
  }

  function discountEntries(cart) {
    var entries = [];
    function add(list) {
      if (!Array.isArray(list)) return;
      list.forEach(function(entry) {
        if (entry) entries.push(entry);
      });
    }
    if (!cart) return entries;
    add(cart.discount_codes);
    add(cart.discountCodes);
    add(cart.cart_level_discount_applications);
    add(cart.discount_applications);
    (cart.items || []).forEach(function(item) {
      add(item && item.discounts);
      add(item && item.line_level_discount_allocations);
    });
    return entries;
  }

  function discountEntryCode(entry) {
    if (!entry) return '';
    var application = entry.discount_application || entry.discountApplication || {};
    return normalizeDiscountCode(entry.code || entry.title || entry.name || application.code || application.title || '');
  }

  function cartExplicitlyHasDiscount(cart, code) {
    code = normalizeDiscountCode(code).toLowerCase();
    if (!code) return false;
    return discountEntries(cart).some(function(entry) {
      var entryCode = discountEntryCode(entry).toLowerCase();
      return entryCode === code && entry.applicable !== false;
    });
  }

  function discountCodeApplicability(cart, code) {
    code = normalizeDiscountCode(code).toLowerCase();
    if (!code || !cart) return null;
    var lists = [cart.discount_codes, cart.discountCodes];
    for (var i = 0; i < lists.length; i += 1) {
      var list = lists[i];
      if (!Array.isArray(list)) continue;
      for (var j = 0; j < list.length; j += 1) {
        var entry = list[j] || {};
        if (discountEntryCode(entry).toLowerCase() !== code) continue;
        if (entry.applicable === false) return false;
        return true;
      }
    }
    return null;
  }

  function inferAppliedDiscountCode(cart) {
    if (!cart) return '';
    var lists = [cart.discount_codes, cart.discountCodes];
    for (var i = 0; i < lists.length; i += 1) {
      var list = lists[i];
      if (!Array.isArray(list)) continue;
      for (var j = 0; j < list.length; j += 1) {
        var statusEntry = list[j] || {};
        var statusCode = discountEntryCode(statusEntry);
        if (statusCode && statusEntry.applicable !== false) return statusCode;
      }
    }

    var entries = discountEntries(cart);
    for (var k = 0; k < entries.length; k += 1) {
      var entry = entries[k] || {};
      var application = entry.discount_application || entry.discountApplication || {};
      var type = String(entry.type || application.type || '').toLowerCase();
      var code = discountEntryCode(entry);
      if (code && type === 'discount_code') return code;
    }
    return '';
  }

  function rugQuantity(cart) {
    var qty = 0;
    (cart && cart.items || []).forEach(function(item) {
      if (isRug(item)) qty += Number(item.quantity || 0);
    });
    return qty;
  }

  function configuredCouponCodes() {
    var box = qs('[data-jh-smart-coupon]');
    return {
      one: normalizeDiscountCode(box && box.getAttribute('data-one-code') || '10OFF'),
      two: normalizeDiscountCode(box && box.getAttribute('data-two-code') || '20OFF')
    };
  }

  function appliedDiscountCodes(cart, includeStateFallback) {
    var codes = [];
    function add(code) {
      code = normalizeDiscountCode(code);
      if (!code) return;
      var lower = code.toLowerCase();
      if (!codes.some(function(existing) { return existing.toLowerCase() === lower; })) codes.push(code);
    }
    var lists = [cart && cart.discount_codes, cart && cart.discountCodes];
    lists.forEach(function(list) {
      if (!Array.isArray(list)) return;
      list.forEach(function(entry) {
        if (entry && entry.applicable !== false) add(discountEntryCode(entry));
      });
    });
    (cart && cart.cart_level_discount_applications || []).forEach(function(entry) {
      var application = entry && (entry.discount_application || entry.discountApplication) || {};
      var type = String(entry && entry.type || application.type || '').toLowerCase();
      if (type === 'discount_code' || entry && entry.code || application.code) add(discountEntryCode(entry));
    });
    if (includeStateFallback !== false && !codes.length && state.appliedDiscountCode) add(state.appliedDiscountCode);
    return codes;
  }

  function couponUpgradeDetails(cart) {
    var codes = configuredCouponCodes();
    var applied = appliedDiscountCodes(cart);
    var oneLower = codes.one.toLowerCase();
    var twoLower = codes.two.toLowerCase();
    return {
      one: codes.one,
      two: codes.two,
      rugQty: rugQuantity(cart),
      appliedCodes: applied,
      hasOne: !!oneLower && applied.some(function(code) { return code.toLowerCase() === oneLower; }),
      hasTwo: !!twoLower && applied.some(function(code) { return code.toLowerCase() === twoLower; }),
      remainingCodes: applied.filter(function(code) { return code.toLowerCase() !== oneLower; })
    };
  }

  function scheduleLowerCouponRemoval(cart) {
    var details = couponUpgradeDetails(cart);
    if (details.rugQty < 2 || !details.hasOne || details.hasTwo || !details.two) return false;
    if (state.couponUpgradePending) return true;
    if (state.couponUpgradeFailedSignature && state.couponUpgradeFailedSignature === state.cartSignature) return false;

    state.couponUpgradePending = true;
    state.couponUpgradeNoticeCode = details.two;

    window.setTimeout(function() {
      queueMutation(function() {
        return request(route('cart/update.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ discount: details.remainingCodes.join(',') })
        }).then(getCart).then(function(updatedCart) {
          var lowerCode = details.one.toLowerCase();
          var stillApplied = appliedDiscountCodes(updatedCart, false).some(function(code) { return code.toLowerCase() === lowerCode; });
          if (stillApplied) throw new Error('Lower coupon remained applied');
          return updatedCart;
        });
      }).then(function(updatedCart) {
        state.couponUpgradePending = false;
        state.couponUpgradeFailedSignature = '';
        clearAppliedDiscountState();
        sync(updatedCart);
        var message = details.one + ' was removed because ' + details.two + ' gives the higher rug saving. Copy ' + details.two + ' and apply it when ready.';
        qsa('[data-jh-cart-status]').forEach(function(status) { status.textContent = message; });
      }).catch(function(error) {
        state.couponUpgradePending = false;
        state.couponUpgradeFailedSignature = state.cartSignature;
        updateCoupons(state.cart || cart);
        qsa('[data-jh-cart-status]').forEach(function(status) {
          status.textContent = details.two + ' is unlocked. Applying it will replace ' + details.one + '.';
        });
        try { console.warn('[JH coupon upgrade]', error); } catch (ignored) {}
      });
    }, 0);
    return true;
  }

  function knownCouponMessage(code, cart) {
    var lower = normalizeDiscountCode(code).toLowerCase();
    var storewide = {};
    var multiRug = {};
    qsa('[data-jh-smart-coupon]').forEach(function(box) {
      var one = normalizeDiscountCode(box.getAttribute('data-one-code') || '10OFF').toLowerCase();
      var two = normalizeDiscountCode(box.getAttribute('data-two-code') || '20OFF').toLowerCase();
      if (one) storewide[one] = true;
      if (two) multiRug[two] = true;
    });
    if (multiRug[lower] && rugQuantity(cart) < 2) {
      return 'This code requires at least 2 rug quantities in the cart. Other items do not count toward the 2-rug requirement.';
    }
    if (storewide[lower]) {
      return 'Shopify did not apply ' + code + '. Confirm that the code is active, available to the Online Store, and not blocked by another discount.';
    }
    return 'Shopify did not apply ' + code + '. The code may be invalid, expired, unavailable for this customer, or the cart may not meet its conditions.';
  }

  function setCouponMessage(titleText, messageText, code) {
    qsa('[data-jh-smart-coupon]').forEach(function(box) {
      var codeEl = qs('[data-jh-smart-coupon-code]', box);
      var title = qs('[data-jh-smart-coupon-title]', box);
      var message = qs('[data-jh-smart-coupon-message]', box);
      if (codeEl && code) codeEl.textContent = code;
      if (title) title.textContent = titleText;
      if (message) message.textContent = messageText;
    });
  }

  function showCouponApplied(code, cart, previousDiscountAmount) {
    code = normalizeDiscountCode(code);
    if (!code) return;
    var totalSaved = discountAmount(cart);
    var newlySaved = Math.max(0, totalSaved - Number(previousDiscountAmount || 0));
    var message;
    if (newlySaved > 0) {
      message = 'Code ' + code + ' is applied. Cart savings increased by ' + money(newlySaved) + '. Total cart savings: ' + money(totalSaved) + '.';
    } else if (totalSaved > 0) {
      message = 'Code ' + code + ' was accepted by Shopify. Current cart savings: ' + money(totalSaved) + '.';
    } else {
      message = 'Code ' + code + ' was accepted by Shopify. Any shipping or checkout-only benefit will appear at checkout.';
    }
    setCouponMessage('Coupon applied', message, code);
    updateCouponEntries(code, true);
    qsa('[data-jh-cart-status]').forEach(function(status) {
      status.textContent = message;
    });
  }

  function showCouponError(code, message) {
    clearAppliedDiscountState();
    setCouponMessage('Coupon not applied', message || ('Shopify did not apply code ' + code + '. Check that the code is active and the cart meets its conditions.'), code);
    updateCouponEntries('', false);
    qsa('[data-jh-smart-coupon-input]').forEach(function(input) {
      if (!input.value) input.value = code;
      input.setAttribute('aria-invalid', 'true');
    });
    qsa('[data-jh-cart-status]').forEach(function(status) {
      status.textContent = message || ('Coupon ' + code + ' was not applied.');
    });
  }

  function restoreAppliedCoupon() {
    // 18AE17-19 stored a browser-only "Applied" state even when Shopify had
    // not accepted the code. Remove that legacy state; Shopify's cart response
    // is now the only source of truth.
    clearAppliedDiscountState();
  }

  function applyCoupon(root, trigger) {
    root = root || doc;
    var input = qs('[data-jh-smart-coupon-input]', root) || qs('[data-jh-smart-coupon-input]');
    var code = normalizeDiscountCode(input ? input.value : '');
    if (!code) {
      var codeEl = qs('[data-jh-smart-coupon-code]', root) || qs('[data-jh-smart-coupon-code]');
      code = normalizeDiscountCode(codeEl ? codeEl.textContent : '');
    }
    if (!code) return;

    if (trigger) {
      if (trigger.getAttribute('data-jh-applying') === 'true') return;
      trigger.setAttribute('data-jh-applying', 'true');
      trigger.disabled = true;
      trigger.textContent = 'Applying…';
    }
    qsa('[data-jh-smart-coupon-input]').forEach(function(field) {
      field.removeAttribute('aria-invalid');
    });

    var beforeAmount = discountAmount(state.cart);
    return settleCartBeforeNavigation(false).then(function() {
      beforeAmount = discountAmount(state.cart);
      return queueMutation(function() {
        return request(route('cart/update.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ discount: code })
        }).then(function() {
          // Re-read the persisted cart once. Some Shopify stores return the
          // mutation response before all discount allocations are reflected.
          return getCart();
        });
      });
    }).then(function(cart) {
      var afterAmount = discountAmount(cart);
      var applicability = discountCodeApplicability(cart, code);
      var explicitMatch = cartExplicitlyHasDiscount(cart, code);
      var amountIncreased = afterAmount > beforeAmount;
      var accepted = applicability === true || explicitMatch || amountIncreased;

      if (!accepted) {
        sync(cart);
        showCouponError(code, knownCouponMessage(code, cart));
        return;
      }

      persistAppliedDiscount(code);
      sync(cart);
      showCouponApplied(code, cart, beforeAmount);
    }).catch(function(error) {
      showCouponError(code, 'The coupon could not be applied. Please try again before checkout.');
      try {
        console.warn('[JH coupon]', error);
      } catch (ignored) {}
    }).finally(function() {
      if (trigger && document.documentElement.contains(trigger)) {
        trigger.removeAttribute('data-jh-applying');
        trigger.disabled = false;
        if (!state.appliedDiscountCode) trigger.textContent = 'Apply';
      }
    });
  }

  function copyCoupon(btn) {
    var root = btn.closest('[data-jh-smart-coupon]') || doc;
    var codeEl = qs('[data-jh-smart-coupon-code]', root);
    var code = normalizeDiscountCode(codeEl ? codeEl.textContent : '');
    if (!code) return;

    qsa('[data-jh-smart-coupon-input]').forEach(function(input) {
      input.value = code;
      input.removeAttribute('aria-invalid');
    });

    function done() {
      var old = btn.textContent;
      btn.setAttribute('data-jh-copying', 'true');
      btn.textContent = 'Copied';
      qsa('[data-jh-cart-status]').forEach(function(status) {
        status.textContent = code + ' copied and placed in the coupon field. Select Apply to use it.';
      });
      setTimeout(function() {
        btn.removeAttribute('data-jh-copying');
        btn.textContent = old || ('Copy ' + code);
      }, 1300);
    }
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(done).catch(done);
    else done();
  }

  var debouncedNote = debounce(function(text) {
    updateNote(text);
  }, 450);
  function mirrorVisibleLineQuantity(input) {
    var line = input && input.closest && input.closest('[data-jh-smart-cart-line]');
    var visibleQty = line && qs('[data-jh-current-quantity]', line);
    if (visibleQty) visibleQty.textContent = String(Math.max(0, parseInt(input.value, 10) || 0));
  }

  var debouncedQty = debounce(function(input) {
    var wrap = input.closest('[data-jh-smart-qty]');
    if (!wrap) return;
    mirrorVisibleLineQuantity(input);
    changeItem(wrap.getAttribute('data-key'), input.value);
  }, 300);

  function bind() {
    restoreAppliedCoupon();
    doc.addEventListener('click', function(e) {
      var checkoutLink = e.target.closest && e.target.closest('[data-jh-smart-cart-checkout],a.jh-smart-cart-checkout[href*="/checkout"]');
      if (checkoutLink && checkoutLink.tagName === 'A') {
        if (e.button && e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (checkoutLink.getAttribute('data-jh-checkout-navigating') === 'true') return;
        checkoutLink.setAttribute('data-jh-checkout-navigating', 'true');
        var checkoutDestination = checkoutLink.getAttribute('href') || route('checkout');
        qsa('[data-jh-cart-status]').forEach(function(status) {
          status.textContent = 'Saving the latest sizes and quantities before checkout…';
        });
        settleCartBeforeNavigation(false).then(function() {
          window.location.assign(checkoutDestination);
        });
        return;
      }

      var fullLink = e.target.closest && e.target.closest('[data-jh-smart-cart-link]');
      if (fullLink) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (fullLink.getAttribute('data-jh-cart-navigating') === 'true') return;
        fullLink.setAttribute('data-jh-cart-navigating', 'true');
        var fullCartDestination = fullLink.getAttribute('href') || route('cart');
        qsa('[data-jh-cart-status]').forEach(function(status) {
          status.textContent = 'Saving the latest sizes and quantities before opening the full cart…';
        });
        settleCartBeforeNavigation(true).then(function() {
          window.location.assign(fullCartDestination);
        });
        return;
      }

      var close = e.target.closest && e.target.closest('[data-jh-smart-cart-close]');
      if (close) {
        e.preventDefault();
        e.stopImmediatePropagation();
        closeDrawer();
        return;
      }

      var opener = e.target.closest && e.target.closest('[data-jh-smart-cart-open],[data-jh-cart-open],[data-jh-mini-cart]');
      if (opener) {
        e.preventDefault();
        e.stopImmediatePropagation();
        getCart().then(function(cart) {
          sync(cart);
          openDrawer(opener);
        }).catch(function() {
          window.location.href = route('cart');
        });
        return;
      }

      var unavailableVariantSummary = e.target.closest && e.target.closest('[data-jh-cart-variant-summary][aria-disabled="true"],[data-jh-cart-add-variant-summary][aria-disabled="true"]');
      if (unavailableVariantSummary) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      var currentVariantOption = e.target.closest && e.target.closest('[data-jh-cart-variant-option]');
      if (currentVariantOption) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (currentVariantOption.disabled) return;
        var currentMenu = currentVariantOption.closest('[data-jh-cart-variant-menu]');
        var currentSummary = currentMenu && qs('[data-jh-cart-variant-summary]', currentMenu);
        if (!currentSummary || currentSummary.getAttribute('data-hydrated') === 'false') return;
        var currentKey = currentVariantOption.getAttribute('data-key') || (currentMenu && currentMenu.getAttribute('data-key'));
        var currentVariantId = currentVariantOption.getAttribute('data-variant-id');
        var currentTitle = currentVariantOption.getAttribute('data-variant-title') || currentVariantOption.textContent.trim();
        if (currentMenu) currentMenu.open = false;
        var currentText = currentSummary && qs('[data-jh-cart-variant-summary-text]', currentSummary);
        if (currentText) currentText.textContent = currentTitle;
        var currentLine = currentVariantOption.closest('[data-jh-smart-cart-line]');
        var currentVisibleText = currentLine && qs('[data-jh-current-variant-text]', currentLine);
        if (currentVisibleText) currentVisibleText.textContent = currentTitle;
        replaceItemVariant(currentKey, currentVariantId, null).catch(function() {});
        return;
      }

      var addVariantOption = e.target.closest && e.target.closest('[data-jh-cart-add-variant-option]');
      if (addVariantOption) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (addVariantOption.disabled) return;
        var addVariantMenu = addVariantOption.closest('[data-jh-cart-add-variant-menu]');
        var addVariantLine = addVariantOption.closest('[data-jh-smart-cart-line]');
        var addVariantSummary = addVariantMenu && qs('[data-jh-cart-add-variant-summary]', addVariantMenu);
        var addVariantText = addVariantSummary && qs('[data-jh-cart-add-variant-summary-text]', addVariantSummary);
        var addVariantButton = addVariantLine && qs('[data-jh-cart-add-size]', addVariantLine);
        qsa('[data-jh-cart-add-variant-option]', addVariantMenu || addVariantLine).forEach(function(option) {
          var selected = option === addVariantOption;
          option.classList.toggle('is-selected', selected);
          option.setAttribute('aria-selected', selected ? 'true' : 'false');
        });
        if (addVariantText) addVariantText.textContent = addVariantOption.getAttribute('data-variant-title') || addVariantOption.textContent.trim();
        if (addVariantButton) {
          addVariantButton.setAttribute('data-selected-variant-id', addVariantOption.getAttribute('data-variant-id') || '');
          addVariantButton.disabled = !addVariantButton.getAttribute('data-selected-variant-id');
        }
        if (addVariantMenu) addVariantMenu.open = false;
        return;
      }

      var addQtyPlus = e.target.closest && e.target.closest('[data-jh-add-size-qty-plus]');
      var addQtyMinus = e.target.closest && e.target.closest('[data-jh-add-size-qty-minus]');
      if (addQtyPlus || addQtyMinus) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var addQtyWrap = e.target.closest('[data-jh-add-size-qty]');
        var addQtyInput = addQtyWrap && qs('[data-jh-add-size-qty-input]', addQtyWrap);
        if (!addQtyInput) return;
        addQtyInput.value = Math.max(1, (parseInt(addQtyInput.value, 10) || 1) + (addQtyPlus ? 1 : -1));
        return;
      }

      var addSize = e.target.closest && e.target.closest('[data-jh-cart-add-size]');
      if (addSize) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var addLine = addSize.closest('[data-jh-smart-cart-line]');
        var addInput = addLine && qs('[data-jh-add-size-qty-input]', addLine);
        var addKey = addSize.getAttribute('data-key') || (addLine && addLine.getAttribute('data-key'));
        var selectedAdditionalVariant = addSize.getAttribute('data-selected-variant-id');
        if (!selectedAdditionalVariant || addSize.disabled) return;
        addAnotherVariantFromLine(addKey, selectedAdditionalVariant, addInput && addInput.value, addSize).then(function() {
          if (!document.documentElement.contains(addSize)) return;
          addSize.setAttribute('data-selected-variant-id', '');
          addSize.disabled = true;
          var summaryText = addLine && qs('[data-jh-cart-add-variant-summary-text]', addLine);
          if (summaryText) summaryText.textContent = 'Select variation';
          qsa('[data-jh-cart-add-variant-option]', addLine).forEach(function(option) {
            option.classList.remove('is-selected');
            option.setAttribute('aria-selected', 'false');
          });
          if (addInput) addInput.value = '1';
        }).catch(function() {});
        return;
      }

      var plus = e.target.closest && e.target.closest('[data-jh-smart-qty-plus]');
      var minus = e.target.closest && e.target.closest('[data-jh-smart-qty-minus]');
      if (plus || minus) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var wrap = e.target.closest('[data-jh-smart-qty]');
        var input = wrap && qs('[data-jh-smart-qty-input]', wrap);
        if (!wrap || !input) return;
        var qty = Math.max(0, (parseInt(input.value, 10) || 0) + (plus ? 1 : -1));
        input.value = qty;
        mirrorVisibleLineQuantity(input);
        changeItem(wrap.getAttribute('data-key'), qty);
        return;
      }

      var remove = e.target.closest && e.target.closest('[data-jh-smart-remove]');
      if (remove) {
        e.preventDefault();
        e.stopImmediatePropagation();
        changeItem(remove.getAttribute('data-key'), 0);
        return;
      }

      var add = e.target.closest && e.target.closest('[data-jh-smart-add]');
      if (add) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var recCard = add.closest('[data-jh-smart-rec-card]');
        var recSelect = recCard && qs('[data-jh-smart-rec-variant-select]', recCard);
        var selectedVariantId = recSelect && recSelect.value ? recSelect.value : add.getAttribute('data-variant-id');
        if (!selectedVariantId) return;
        add.setAttribute('data-variant-id', selectedVariantId);
        var old = add.textContent;
        add.disabled = true;
        add.textContent = 'Adding…';
        addVariant(selectedVariantId, 1, { opener: add }).then(function() {
          add.textContent = 'Added';
        }).finally(function() {
          setTimeout(function() {
            add.disabled = false;
            add.textContent = old || 'Add';
          }, 900);
        });
        return;
      }

      var couponApply = e.target.closest && e.target.closest('[data-jh-smart-coupon-apply]');
      if (couponApply) {
        e.preventDefault();
        e.stopImmediatePropagation();
        applyCoupon(couponApply.closest('.jh-smart-coupon-entry') || doc, couponApply);
        return;
      }

      var copy = e.target.closest && e.target.closest('[data-jh-smart-copy]');
      if (copy) {
        e.preventDefault();
        e.stopImmediatePropagation();
        copyCoupon(copy);
        return;
      }
    }, true);

    doc.addEventListener('change', function(e) {
      var cartVariant = e.target && e.target.closest && e.target.closest('[data-jh-cart-variant-select]');
      if (cartVariant) {
        e.preventDefault();
        e.stopImmediatePropagation();
        replaceItemVariant(cartVariant.getAttribute('data-key'), cartVariant.value, cartVariant);
        return;
      }
      var recVariant = e.target && e.target.closest && e.target.closest('[data-jh-smart-rec-variant-select]');
      if (recVariant) {
        var recCard = recVariant.closest('[data-jh-smart-rec-card]');
        var option = recVariant.options[recVariant.selectedIndex];
        var recButton = recCard && qs('[data-jh-smart-add]', recCard);
        var recPrice = recCard && qs('[data-jh-smart-rec-price]', recCard);
        var price = Number(option && option.getAttribute('data-price') || 0);
        var compare = Number(option && option.getAttribute('data-compare-price') || 0);
        if (recButton) recButton.setAttribute('data-variant-id', recVariant.value);
        if (recPrice && price) recPrice.innerHTML = money(price) + (compare > price ? '<s>' + money(compare) + '</s>' : '');
        return;
      }
      var addSizeInput = e.target && e.target.closest && e.target.closest('[data-jh-add-size-qty-input]');
      if (addSizeInput) {
        addSizeInput.value = Math.max(1, parseInt(addSizeInput.value, 10) || 1);
        return;
      }
      var input = e.target && e.target.closest && e.target.closest('[data-jh-smart-qty-input]');
      if (input) {
        e.preventDefault();
        e.stopImmediatePropagation();
        debouncedQty(input);
        return;
      }
      var note = e.target && e.target.closest && e.target.closest('[data-jh-smart-cart-note]');
      if (note) {
        debouncedNote(note.value);
      }
    }, true);

    doc.addEventListener('input', function(e) {
      var input = e.target && e.target.closest && e.target.closest('[data-jh-smart-qty-input]');
      if (input) {
        mirrorVisibleLineQuantity(input);
        debouncedQty(input);
        return;
      }
      var note = e.target && e.target.closest && e.target.closest('[data-jh-smart-cart-note]');
      if (note) debouncedNote(note.value);
    }, true);

    doc.addEventListener('submit', function(e) {
      var form = e.target && e.target.closest && e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      addForm(form);
    }, true);

    doc.addEventListener('keydown', function(e) {
      var couponInput = e.target && e.target.closest && e.target.closest('[data-jh-smart-coupon-input]');
      if (couponInput && e.key === 'Enter') {
        e.preventDefault();
        e.stopImmediatePropagation();
        var couponEntry = couponInput.closest('.jh-smart-coupon-entry') || doc;
        var couponButton = qs('[data-jh-smart-coupon-apply]', couponEntry);
        applyCoupon(couponEntry, couponButton);
        return;
      }
      var drawer = qs('[data-jh-smart-cart-drawer]') || qs('[data-jh-cart-drawer]');
      if (!drawer || drawer.getAttribute('aria-hidden') === 'true') return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
      } else if (e.key === 'Tab' && window.JH && JH.A11y) {
        JH.A11y.trapTab(e, drawer);
      }
    });

    qsa('[data-jh-smart-cart-link]').forEach(function(link) {
      link.setAttribute('href', route('cart'));
    });

    if (window.JH) {
      window.JH.Cart = window.JH.Cart || {};
      window.JH.Cart.get = getCart;
      window.JH.Cart.fetch = getCart;
      window.JH.Cart.renderDrawer = sync;
      window.JH.Cart.renderCount = updateCounts;
      window.JH.Cart.open = openDrawer;
      window.JH.Cart.close = closeDrawer;
      window.JH.Cart.add = addVariant;
      window.JH.Cart.addItems = addItems;
      window.JH.Cart.addSilent = addVariantSilently;
      window.JH.Cart.change = changeItem;
      window.JH.Cart.changeVariant = replaceItemVariant;
      window.JH.Cart.addAnotherVariant = addAnotherVariantFromLine;
      window.JH.Cart.refresh = refreshCartFromServer;
      window.JH.Cart.whenIdle = whenCartIdle;
    }

    bindCartSynchronization();
    getCart().then(sync).catch(function() {
      updateCoupons({
        items: [],
        item_count: 0,
        total_price: 0
      });
    });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', bind, {
    once: true
  });
  else bind();
  window.JHSmartCart = {
    get: getCart,
    sync: sync,
    open: openDrawer,
    close: closeDrawer,
    add: addVariant,
    addItems: addItems,
    addSilent: addVariantSilently,
    change: changeItem,
    changeVariant: replaceItemVariant,
    addAnotherVariant: addAnotherVariantFromLine,
    refresh: refreshCartFromServer,
    whenIdle: whenCartIdle
  };
})();
